import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useAuth } from '../../auth'
import { api, ApiFailure, type ClinicSettings, type Doctor, type QueueRow } from '../../api'
import { EmptyState, Loading, MobileCtx, type MobileState, Screen, type Toast, TopNav, useRoleFlags } from './shared'
import Lock, { isLocked } from './Lock'
import AppSettings from './AppSettings'
import { useT } from './i18n'

const Home = lazy(() => import('./Home'))
const DoctorsList = lazy(() => import('./Doctors'))
const DoctorDetail = lazy(() => import('./Doctors').then((m) => ({ default: m.DoctorDetail })))
const Book = lazy(() => import('./Book'))
const QuickBill = lazy(() => import('./QuickBill'))
const Bookings = lazy(() => import('./Bookings'))
const Patients = lazy(() => import('./Patients'))
const PatientDetail = lazy(() => import('./Patients').then((m) => ({ default: m.PatientDetail })))
const Profile = lazy(() => import('./Profile'))
const Visit = lazy(() => import('./Visit'))
const MyVisit = lazy(() => import('./MyVisit'))
const Console = lazy(() => import('./Console'))
const Records = lazy(() => import('./Records'))
const Reports = lazy(() => import('./Reports'))
const Admin = lazy(() => import('./Admin'))
const Settings = lazy(() => import('./Settings'))
const Staff = lazy(() => import('./Staff'))
const StaffDetail = lazy(() => import('./Staff').then((m) => ({ default: m.StaffDetail })))
const Roster = lazy(() => import('./Roster'))
const ChangePassword = lazy(() => import('./ChangePassword'))
const ChangePhone = lazy(() => import('./ChangePhone'))
const Welcome = lazy(() => import('./Welcome'))
const Notifications = lazy(() => import('./Notifications'))
const Help = lazy(() => import('./Help'))
const Schedule = lazy(() => import('./Schedule'))
const PatientEdit = lazy(() => import('./PatientEdit'))
const MyDetails = lazy(() => import('./MyDetails'))
const DayEnd = lazy(() => import('./DayEnd'))
const Billing = lazy(() => import('./Billing'))
const Lab = lazy(() => import('./Lab'))
const Stock = lazy(() => import('./Stock'))
const StockItem = lazy(() => import('./StockItem'))
const StockReports = lazy(() => import('./StockReports'))
const LabSheet = lazy(() => import('./LabSheet'))
const StockAlerts = lazy(() => import('./StockAlerts'))
const Search = lazy(() => import('./Search'))
const Account = lazy(() => import('./Account'))
const AppointmentDetail = lazy(() => import('./AppointmentDetail'))

/* ------------------------------------------------------------------ */
/* Mobile app page (/m).                                                */
/*                                                                      */
/* A phone-first face on the same API and the same session cookie as    */
/* the desktop screens, laid out after the MediCare mobile UI kit.      */
/* Nothing here is a second copy of the business rules - reception,     */
/* scheduling, the pad and the records layer all go through the same    */
/* endpoints, so the two faces can never drift apart.                   */
/*                                                                      */
/* This file is the router and the shared store. Every screen lives in  */
/* its own file beside it and pulls its furniture from ./shared.        */
/* ------------------------------------------------------------------ */

/** Mirrors the desktop route guards: a screen the role cannot reach says so.
 *  It keeps the chrome, because a bare card with no tab bar strands the user.
 *  The wording matches the desktop guard exactly, full stop included. */
function Gate({ allow, children }: { allow: boolean; children: React.ReactNode }) {
  const { t } = useT()
  if (!allow) {
    return (
      <Screen>
        <TopNav title={t('shell.notAvailable')} />
        <EmptyState title={t('shell.noAccessTitle')}>{t('shell.noAccessBody')}</EmptyState>
      </Screen>
    )
  }
  return <>{children}</>
}

/**
 * The patient record: staff see anyone, a patient sees only themselves.
 * The route takes an id, so the check has to happen inside the route rather
 * than on the element, and the server enforces the same rule either way.
 */
function OwnRecordGate({ isStaff, patientId, children }: {
  isStaff: boolean; patientId: string | null | undefined; children: React.ReactNode
}) {
  const { id } = useParams<{ id: string }>()
  return <Gate allow={isStaff || (!!patientId && id === patientId)}>{children}</Gate>
}

export default function Mobile() {
  const { user, can } = useAuth()
  const { t } = useT()
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [settings, setSettings] = useState<ClinicSettings | null>(null)
  const [queue, setQueue] = useState<QueueRow[]>([])
  const [toastState, setToastState] = useState<Toast | null>(null)

  // The chosen theme has to be applied wherever the app is entered, not only
  // while the settings screen happens to be mounted.
  useEffect(() => {
    const apply = () => {
      let pref = 'system'
      try { pref = localStorage.getItem('clinic.m.theme') ?? 'system' } catch { pref = 'system' }
      const dark = pref === 'dark'
        || (pref === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
      const el = document.querySelector('.mapp')
      if (!el) return
      if (dark) el.setAttribute('data-theme', 'dark')
      else el.removeAttribute('data-theme')
    }
    apply()
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  // A shared tablet locks itself between patients. The check runs once per
  // mount rather than on every render, so unlocking is not undone instantly.
  const [locked, setLocked] = useState(() => isLocked())
  useEffect(() => {
    if (!locked) return
    if (window.location.pathname !== '/m/lock') window.location.replace('/m/lock')
    setLocked(false)
  }, [locked])

  const { isAdmin, isClinical, isPatient, isReceptionist, isLabManager, canViewStock, canBill, canReports } = useRoleFlags()
  // The lab manager is staff of the lab, not of the front desk: no patient
  // register, billing or queue, so those screens and their requests stay shut.
  const isStaff = !isPatient && !isLabManager

  /*
   * Offline tolerance. The last queue, roster and clinic settings are kept on
   * the device, per signed-in user, so a Wi-Fi drop at the desk leaves the day
   * readable instead of blank. Anything shown from the cache says so, with the
   * time it was saved, because a queue that is ten minutes old is wrong.
   */
  const cacheKey = `clinic.m.cache.${user?.id ?? 'anon'}`
  const [offlineSince, setOfflineSince] = useState<string | null>(null)
  const saveCache = useCallback((patch: Record<string, unknown>) => {
    try {
      const prev = JSON.parse(localStorage.getItem(cacheKey) ?? '{}') as Record<string, unknown>
      localStorage.setItem(cacheKey, JSON.stringify({ ...prev, ...patch, savedAt: new Date().toISOString() }))
    } catch { /* storage full or blocked: the cache is a convenience */ }
  }, [cacheKey])
  const readCache = useCallback((): {
    queue?: QueueRow[]; doctors?: Doctor[]; settings?: ClinicSettings | null; savedAt?: string
  } => {
    try { return JSON.parse(localStorage.getItem(cacheKey) ?? '{}') } catch { return {} }
  }, [cacheKey])

  const reloadQueue = useCallback(async () => {
    // The queue is a staff-only endpoint, so a patient asking for it gets a
    // 403 that only pollutes the console - the patient tab bar never shows it.
    if (isPatient || isLabManager) return
    try {
      const r = await api.get<{ visits: QueueRow[] }>('/api/visits')
      setQueue(r.visits)
      saveCache({ queue: r.visits })
      setOfflineSince(null)
    } catch (err) {
      // Only an unreachable or failing server falls back to the cache; a refusal
      // (401, 403) is a real answer and is passed on.
      if (!navigator.onLine || (err instanceof ApiFailure && (err.status === 0 || err.status >= 500))) {
        const c = readCache()
        if (c.queue) setQueue(c.queue)
        setOfflineSince(c.savedAt ?? new Date().toISOString())
        return
      }
      throw err
    }
  }, [isPatient, isLabManager, saveCache, readCache])

  // Back online: refresh straight away rather than waiting for the next tick.
  useEffect(() => {
    const on = () => { void reloadQueue().catch(() => { /* next tick retries */ }) }
    const off = () => setOfflineSince((s) => s ?? readCache().savedAt ?? new Date().toISOString())
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off) }
  }, [reloadQueue, readCache])

  useEffect(() => {
    void (async () => {
      const [d, s] = await Promise.allSettled([
        api.get<{ doctors: Doctor[] }>('/api/doctors'),
        api.get<{ settings: ClinicSettings | null }>('/api/settings'),
      ])
      const cached = readCache()
      if (d.status === 'fulfilled') {
        setDoctors(d.value.doctors.filter((x) => x.isActive))
        saveCache({ doctors: d.value.doctors })
      } else if (cached.doctors) {
        setDoctors(cached.doctors.filter((x) => x.isActive))
      }
      if (s.status === 'fulfilled') {
        setSettings(s.value.settings)
        saveCache({ settings: s.value.settings })
      } else if (cached.settings !== undefined) {
        setSettings(cached.settings)
      }
      await reloadQueue().catch(() => { /* queue is best-effort on first paint */ })
    })()
  }, [reloadQueue, readCache, saveCache])

  // The queue is watched from the desk and the consulting room at once, so it
  // refreshes itself rather than waiting for someone to pull it.
  useEffect(() => {
    if (isPatient || isLabManager) return
    const t = setInterval(() => { void reloadQueue().catch(() => { /* next tick will retry */ }) }, 30_000)
    return () => clearInterval(t)
  }, [isPatient, isLabManager, reloadQueue])

  useEffect(() => {
    if (!toastState) return
    const t = setTimeout(() => setToastState(null), 3400)
    return () => clearTimeout(t)
  }, [toastState])

  const reloadSettings = useCallback(() => {
    void api.get<{ settings: ClinicSettings | null }>('/api/settings')
      .then((r) => { setSettings(r.settings); saveCache({ settings: r.settings }) })
      .catch(() => { /* the next start reads them again */ })
  }, [saveCache])

  const value = useMemo<MobileState>(
    () => ({ doctors, settings, queue, reloadQueue, reloadSettings, toast: setToastState }),
    [doctors, settings, queue, reloadQueue, reloadSettings],
  )

  return (
    <MobileCtx.Provider value={value}>
      <div className="mapp-desk">
        <div className="mapp">
          <Suspense fallback={<Screen tabs={false}><Loading>{t('shell.loadingScreen')}</Loading></Screen>}>
          <Routes>
            <Route index element={<Home />} />

            {/* scheduling. The roster is open to everyone - a patient should
                be able to look up who is in and when. Taking a booking is
                reception's job, and the server refuses it from a patient, so
                the screen that does it is gated rather than left to fail at
                the last click. */}
            <Route path="doctors" element={<DoctorsList />} />
            <Route path="doctors/:id" element={<DoctorDetail />} />
            <Route path="book/:doctorId" element={<Gate allow={(isStaff || isPatient) && settings?.appointmentsEnabled !== false}><Book /></Gate>} />
            <Route path="appointments" element={isLabManager ? <LabSheet /> : <Bookings />} />
            <Route path="appointments/:id" element={<AppointmentDetail />} />

            {/* the working screens */}
            {/* The desk lives on the patients screen now; keep old links and
                any bookmarked tab working rather than 404ing them. */}
            <Route path="reception" element={<Navigate to="/m/patients" replace />} />
            <Route path="my-visits/:id" element={<Gate allow={isPatient}><MyVisit /></Gate>} />
            <Route path="visits/:id" element={<Gate allow={isStaff}><Visit /></Gate>} />
            <Route path="console" element={<Gate allow={isClinical}><Console /></Gate>} />
            <Route path="patients" element={<Gate allow={isStaff}><Patients /></Gate>} />
            <Route
              path="patients/:id"
              element={(
                <OwnRecordGate isStaff={isStaff} patientId={user?.patientId}>
                  <PatientDetail />
                </OwnRecordGate>
              )}
            />
            <Route path="patients/:id/edit" element={<Gate allow={isStaff}><PatientEdit /></Gate>} />
            {/* A patient's own contact details; the server takes the patient from the session. */}
            <Route path="my-details" element={<Gate allow={can('profile.own')}><MyDetails /></Gate>} />
            <Route path="records" element={<Gate allow={isStaff}><Records /></Gate>} />
            <Route path="reports" element={<Gate allow={canReports}><Reports /></Gate>} />
            {/* The investigations tray is retired from the app; old links land on the lab. */}
            <Route path="investigations" element={<Navigate to="/m/lab" replace />} />
            <Route path="day-end" element={<Gate allow={canBill}><DayEnd /></Gate>} />
            <Route path="billing" element={<Gate allow={canBill}><Billing /></Gate>} />
            <Route path="bill/new" element={<Gate allow={canBill}><QuickBill /></Gate>} />
             <Route path="lab" element={<Gate allow><Lab /></Gate>} />

            {/* stock: read by the doctor, kept by the lab manager and whoever the admin allowed */}
            <Route path="stock" element={<Gate allow={canViewStock}><Stock /></Gate>} />
            <Route path="stock/reports" element={<Gate allow={canViewStock}><StockReports /></Gate>} />
            <Route path="stock/:id" element={<Gate allow={canViewStock}><StockItem /></Gate>} />

            {/* administration */}
            <Route path="admin" element={<Gate allow={isAdmin}><Admin /></Gate>} />
            <Route path="settings" element={<Gate allow={isAdmin}><Settings /></Gate>} />
            {/* Gated by the permission the server checks on every users route, so the
                two can never disagree about who may manage accounts. */}
            <Route path="staff" element={<Gate allow={can('users.manage')}><Staff /></Gate>} />
            <Route path="staff/:id" element={<Gate allow={can('users.manage')}><StaffDetail /></Gate>} />
            <Route path="roster" element={<Gate allow={isAdmin}><Roster /></Gate>} />
            <Route path="schedule" element={<Gate allow={isAdmin || isReceptionist}><Schedule /></Gate>} />

            {/* Both screens are made of desk material - the patient queue, the
                investigations tray, the patient register - so they follow the
                same staff gate as the screens they summarise. A patient who
                typed the URL used to meet a bare 403 toast instead. */}
            <Route path="search" element={<Gate allow={isStaff}><Search /></Gate>} />
            <Route path="account" element={<Account />} />
            <Route path="app-settings" element={<AppSettings />} />
            <Route path="lock" element={<Lock />} />
            <Route path="welcome" element={<Welcome />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="help" element={<Help />} />
            <Route path="profile" element={<Profile />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="change-phone" element={<ChangePhone />} />

            <Route path="*" element={<Navigate to="/m" replace />} />
          </Routes>
          {/* Out, low or running short: put in front of whoever restocks, once a day. */}
          <StockAlerts />
          </Suspense>
          {offlineSince && (
            <div className="m-offline" role="status">
              {t('shell.offlineBanner', {
                time: new Date(offlineSince).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              })}
            </div>
          )}
          {toastState &&<div className={`m-toast ${toastState.kind}`}>{toastState.text}</div>}
        </div>
      </div>
    </MobileCtx.Provider>
  )
}
